const CONSENT_KEY = 'dyt_privacy_policy_consent'
const DOCUMENT_TYPES = ['privacy', 'user_agreement']

function readConsent(storage) {
  try {
    const consent = JSON.parse(storage.getItem(CONSENT_KEY))
    return consent && typeof consent === 'object' && !Array.isArray(consent) ? consent : {}
  } catch (error) {
    return {}
  }
}

function consentMode(documents, consent) {
  if (!documents.length) return null
  if (documents.every(doc => consent[doc.doc_type] === doc.version_no)) return null
  return DOCUMENT_TYPES.some(type => Number.isInteger(consent[type]) && consent[type] > 0) ? 'update' : 'initial'
}

export function hasPrivacyConsent(storage) {
  const version = readConsent(storage).privacy
  return Number.isInteger(version) && version > 0
}

// 仅在首页校验和提示；未同意时暂停离开首页的业务跳转。
export function createPrivacyConsent({ storage, fetchCurrent, onError = () => {}, management = false }) {
  const state = { prompt: null, mode: null, documents: [], changedDocuments: [], loading: false, errorMessage: '', agreed: hasPrivacyConsent(storage) }
  let loaded = false
  let currentRoute = null
  let requestId = 0
  let pendingNavigation = null

  function finishNavigation(allowed) {
    const pending = pendingNavigation
    pendingNavigation = null
    if (pending) {
      if (allowed) pending.proceed()
      else pending.cancel()
    }
  }

  function showPrompt() {
    state.prompt = management || (currentRoute && currentRoute.path === '/home') ? state.mode : null
  }

  async function refresh() {
    const id = ++requestId
    loaded = false
    state.loading = true
    state.prompt = null
    state.errorMessage = ''
    try {
      const documents = await fetchCurrent()
      if (id !== requestId) return
      if (!Array.isArray(documents)) throw new Error('协议版本信息异常，请稍后重试')
      state.documents = documents.filter(doc =>
        doc && DOCUMENT_TYPES.includes(doc.doc_type) && Number.isInteger(doc.version_no) && doc.version_no > 0 && (!management || doc.doc_type === 'privacy')
      )
      if (!state.documents.length) throw new Error('协议数据为空')
      const consent = readConsent(storage)
      state.changedDocuments = management ? state.documents : state.documents.filter(doc => consent[doc.doc_type] !== doc.version_no)
      state.mode = management ? 'update' : consentMode(state.documents, consent)
      loaded = true
      showPrompt()
      if (!state.mode) finishNavigation(true)
    } catch (error) {
      if (id !== requestId) return
      state.documents = []
      state.changedDocuments = []
      state.mode = null
      state.prompt = null
      // 当前首页校验失败后提示并放行，避免反复请求或阻断正常使用；不写同意记录。
      loaded = true
      onError(new Error('协议加载失败，请稍后重试'))
      finishNavigation(true)
    } finally {
      if (id === requestId) state.loading = false
    }
  }

  return {
    state,
    openManagement() {
      if (management) return refresh()
    },
    onRoute(route) {
      finishNavigation(false)
      currentRoute = route
      if (!management && route.path === '/home') return refresh()
      // 离开首页后忽略仍在进行的响应，不在其他页面校验或显示提示。
      ++requestId
      loaded = false
      state.loading = false
      state.prompt = null
      state.mode = null
      state.documents = []
      state.changedDocuments = []
      state.errorMessage = ''
    },
    beforeNavigate(to, from, next, proceed) {
      if (from.path !== '/home' || to.path === '/home') return false
      if (to.meta && to.meta.privacyPolicyDetail) {
        finishNavigation(false)
        return false
      }
      if (loaded && !state.loading && !state.mode) return false
      finishNavigation(false)
      pendingNavigation = { cancel: () => next(false), proceed }
      if (!loaded && !state.loading) refresh()
      else if (!state.loading) showPrompt()
      return true
    },
    reject() {
      if (management) {
        try {
          storage.removeItem(CONSENT_KEY)
        } catch (error) {
          state.errorMessage = '无法撤销同意，请检查浏览器存储后重试'
          onError(new Error(state.errorMessage))
          return
        }
        state.agreed = false
        state.mode = null
        state.prompt = null
        state.errorMessage = ''
        return
      }
      if (state.prompt === 'initial' || state.prompt === 'update') state.prompt = 'refusal'
    },
    decline() {
      state.prompt = null
      finishNavigation(false)
    },
    review() {
      showPrompt()
    },
    accept() {
      if (!loaded || state.loading || !state.documents.length || !state.mode || state.prompt !== state.mode) return false
      const consent = readConsent(storage)
      state.documents.forEach(doc => { consent[doc.doc_type] = doc.version_no })
      try {
        storage.setItem(CONSENT_KEY, JSON.stringify(consent))
      } catch (error) {
        state.errorMessage = '无法保存同意记录，请检查浏览器存储后重试'
        onError(new Error(state.errorMessage))
        return false
      }
      state.mode = null
      state.prompt = null
      state.errorMessage = ''
      state.agreed = hasPrivacyConsent(storage)
      finishNavigation(true)
      return true
    },
    dispose() {
      ++requestId
      finishNavigation(false)
    }
  }
}
